import { useEffect } from 'react';
import { readAudioDuration } from '../utils/read-audio-duration';

type UseAudioDurationProbeOptions = {
  /** URL del audio a probear. Si es null, el callback recibe null. */
  url: string | null;
  /**
   * Si la duración ya es conocida (ej. el backend la devolvió), se usa esa y
   * no se dispara el probe HTTP. Útil para evitar trabajo innecesario.
   */
  knownDurationMs?: number | null;
  /** Recibe la duración resuelta en ms, o null si falla / no hay url. */
  onDurationChange: (durationMs: number | null) => void;
};

/**
 * Hook que dispara un probe de duración en background cuando `url` cambia.
 * Si `knownDurationMs` está presente, se usa sin probear.
 *
 * El probe es ligero: descarga solo el header del archivo (`preload='metadata'`),
 * unos pocos KB. Sirve para que el botón "Aplicar" tenga la duración lista sin
 * que el user tenga que esperar.
 *
 * Usa cancellation con cleanup para evitar setState de un probe stale si la
 * url cambia antes de resolver.
 */
export const useAudioDurationProbe = ({
  url,
  knownDurationMs,
  onDurationChange
}: UseAudioDurationProbeOptions): void => {
  useEffect(() => {
    if (!url) {
      onDurationChange(null);
      return;
    }
    if (typeof knownDurationMs === 'number') {
      onDurationChange(knownDurationMs);
      return;
    }
    let cancelled = false;
    readAudioDuration(url)
      .then(durationSec => {
        if (!cancelled) onDurationChange(Math.round(durationSec * 1000));
      })
      .catch(() => {
        if (!cancelled) onDurationChange(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url, knownDurationMs, onDurationChange]);
};
